import { computed, inject } from '@angular/core';
import { SystemService } from '@map/services/system.service';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntity, upsertEntities, upsertEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { GeoJSONFeature } from 'mapbox-gl';
import { of, pipe, switchMap } from 'rxjs';
import { updateState, withDevtools, withDevToolsStub } from '@angular-architects/ngrx-toolkit';
import { environment } from 'src/environments/environment';

export interface SystemEntry {
    $id: string;
    id: number;
    key: string;
    updatedAt: string;
    value: string;
    xmin: number;
}

export interface AiriaConfigUpdate {
    key: string;
    value: string;
}

type SystemStore = {
    selected: GeoJSONFeature | null;
    selectedForReview: GeoJSONFeature[] | null;
    hoveredFeatureId: string | null;
    loading: boolean;
    loaded: boolean;
};

const initialState: SystemStore = {
    selected: null,
    selectedForReview: null,
    hoveredFeatureId: null,
    loading: false,
    loaded: false,
};

export const withTreeShakableDevTools = environment.production ? withDevToolsStub : withDevtools;

export const SystemStore = signalStore(
    { providedIn: 'root' },
    withTreeShakableDevTools('system'),
    withEntities<SystemEntry>(),
    withState<SystemStore>(initialState),
    withComputed(({ entities }) => ({
        hiddenBuildingsData: computed(() => {
            const entry = entities().find((e) => e.key === 'mapbox_hidden_buildings');
            if (!entry) return { ids: null, entryId: null };
            const value = entry.value.slice(1, -1);
            const ids = value.length > 0 ? value.split(',').map((id) => +id) : [];
            return { ids, entryId: entry.id };
        }),
        hiddenBuildings: computed(() => {
            const entry = entities().find((e) => e.key === 'mapbox_hidden_buildings');
            if (!entry) return null;
            const value = entry.value.slice(1, -1);
            return value.length > 0 ? value.split(',').map((id) => +id) : [];
        }),
        hiddenBuildingsId: computed(() => entities().find((e) => e.key === 'mapbox_hidden_buildings')?.id || null),
    })),
    withMethods((store, systemService = inject(SystemService)) => ({
        select: (system: GeoJSONFeature) => {
            if (store.selected()?.id === system?.id) return;
            updateState(store, '[System] Select', { selected: system });
            return system;
        },
        selectForReview: (systems: GeoJSONFeature[]) => {
            updateState(store, '[System] Select For Review', { selectedForReview: systems });
            return systems;
        },
        deselect: () => {
            updateState(store, '[System] Deselect', { selected: null, selectedForReview: null, hoveredFeatureId: null });
        },
        hoverFeature: (id: string | null) => {
            updateState(store, '[System] Hover Feature', { hoveredFeatureId: id });
        },
        getAll: rxMethod<void>(
            pipe(
                switchMap(() => {
                    if (store.loaded()) return of(store.entities());
                    updateState(store, '[System] Get All', { loading: true });
                    return systemService.getAll<any[]>().pipe(
                        tapResponse({
                            next: (systems) => {
                                updateState(store, '[System] Get All Success', upsertEntities(systems), {
                                    loaded: true,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to load systems', error);
                                updateState(store, '[System] Get All Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        create: rxMethod<{ key: string; value: string }>(
            pipe(
                switchMap((data) => {
                    updateState(store, '[System] Create', { loading: true });
                    const { key, value } = data;
                    return systemService.create<SystemEntry>({ key, value }).pipe(
                        tapResponse({
                            next: (newEntry) => {
                                updateState(store, '[System] Create Success', upsertEntity(newEntry), {
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to create system entry', error);
                                updateState(store, '[System] Create Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        update: rxMethod<{ key: string; value: string }>(
            pipe(
                switchMap((data) => {
                    // Find the existing entry to get its ID
                    const existingEntry = store.entities().find((entry) => entry.key === data.key);
                    if (!existingEntry) {
                        console.error('No existing entry found for key', data.key);
                        return of(data);
                    }
                    updateState(store, '[System] Update', { loading: true });
                    return systemService.update<SystemEntry>(existingEntry.id, { key: data.key, value: data.value }).pipe(
                        tapResponse({
                            next: (updatedEntry) => {
                                updateState(store, '[System] Update Success', upsertEntity(updatedEntry), {
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to update system', error);
                                updateState(store, '[System] Update Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        updateMapboxHiddenBuildings: rxMethod<number>(
            pipe(
                switchMap((id) => {
                    const { ids: hiddenBuildings, entryId } = store.hiddenBuildingsData();

                    if (hiddenBuildings && entryId) {
                        // Use immutable operations instead of mutation
                        const updatedBuildings = hiddenBuildings.includes(id)
                            ? hiddenBuildings.filter((buildingId) => buildingId !== id)
                            : [...hiddenBuildings, id];

                        const updatedValue = `[${updatedBuildings.join(',')}]`;
                        updateState(store, '[System] Update Mapbox Hidden Buildings', { loading: true });
                        return systemService.update<SystemEntry>(entryId, { key: 'mapbox_hidden_buildings', value: updatedValue }).pipe(
                            tapResponse({
                                next: (updatedEntry) => {
                                    updateState(store, '[System] Update Mapbox Hidden Buildings Success', upsertEntity(updatedEntry), {
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to update mapbox hidden buildings', error);
                                    updateState(store, '[System] Update Mapbox Hidden Buildings Failure', { loading: false });
                                },
                            })
                        );
                    } else {
                        updateState(store, '[System] Create Mapbox Hidden Buildings', { loading: true });
                        return systemService.create<SystemEntry>({ key: 'mapbox_hidden_buildings', value: `[${id}]` }).pipe(
                            tapResponse({
                                next: (newEntry) => {
                                    updateState(store, '[System] Create Mapbox Hidden Buildings Success', upsertEntity(newEntry), {
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to create mapbox hidden buildings entry', error);
                                    updateState(store, '[System] Create Mapbox Hidden Buildings Failure', { loading: false });
                                },
                            })
                        );
                    }
                })
            )
        ),
        delete: rxMethod<number>(
            pipe(
                switchMap((id) => {
                    updateState(store, '[System] Delete', { loading: true });
                    return systemService.delete(id).pipe(
                        tapResponse({
                            next: () => {
                                updateState(store, '[System] Delete Success', removeEntity(id), {
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to delete system', error);
                                updateState(store, '[System] Delete Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
    }))
);
